import * as React from 'react';
import { Avatar, Button, Card, Image, InputNumber, Typography, notification } from 'antd';
import { InstagramOutlined, TikTokOutlined, YoutubeFilled } from '@ant-design/icons';
import { formatCompact, formatVIT } from '../format';
import { useSetPostViews } from '../queryHooks';
import type { CampaignPost } from '../types';

const PLATFORM_ICON: Record<string, React.ReactNode> = {
    ig: <InstagramOutlined />,
    yt: <YoutubeFilled />,
    tt: <TikTokOutlined />,
};

const PostCard: React.FC<{
    post: CampaignPost;
    creatorName?: string;
    avatar?: string;
    draft?: boolean;
    onInclude?: (post: CampaignPost) => void;
    onExclude?: (post: CampaignPost) => void;
    onDelete?: (post: CampaignPost) => void;
    busy?: boolean;
}> = ({ post, creatorName, avatar, draft, onInclude, onExclude, onDelete, busy }) => {
    const [broken, setBroken] = React.useState(false);
    const [hover, setHover] = React.useState(false);
    const thumb = post.thumbnail_url || post.media_url;
    const showRemove = hover && post.in_campaign && !!onExclude;

    // Stories: Instagram does not expose views to third parties, so the team
    // types in the number the creator reports. Empty / 0 shows as "—".
    const isStory = post.content_type === 'story';
    const savedViews = post.views > 0 ? post.views : null;
    const [views, setViews] = React.useState<number | null>(savedViews);
    React.useEffect(() => setViews(savedViews), [savedViews]);
    const setPostViews = useSetPostViews();
    const saveViews = async () => {
        if ((views ?? 0) === (savedViews ?? 0)) return;
        try {
            await setPostViews.mutateAsync({ campaignId: post.campaign_id, postId: post.id, views: views ?? 0 });
        } catch {
            setViews(savedViews);
            notification.error({ message: 'Failed to save views' });
        }
    };

    const metricNumber: React.CSSProperties = { fontSize: 16, color: 'var(--text-strong)', fontWeight: 600 };

    return (
        <Card
            size="small"
            style={{ borderRadius: 12, overflow: 'hidden', position: 'relative' }}
            bodyStyle={{ padding: 12 }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Avatar size={28} src={avatar ? `data:image/jpeg;base64,${avatar}` : undefined}>
                    {(creatorName || post.author_username || '?')[0]?.toUpperCase()}
                </Avatar>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <Typography.Text strong ellipsis style={{ display: 'block' }}>
                        {creatorName || post.author_username || 'Creator'}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        {PLATFORM_ICON[post.platform]} @{post.author_username}
                        {post.published_at ? ` · ${new Date(post.published_at).toLocaleDateString()}` : ''}
                    </Typography.Text>
                </div>
            </div>
            <div style={{
                background: 'var(--surface-sunken)',
                borderRadius: 8,
                minHeight: 220,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                marginBottom: 8,
            }}>
                {thumb && !broken ? (
                    <Image
                        src={thumb}
                        alt=""
                        style={{ width: '100%', objectFit: 'cover', maxHeight: 320 }}
                        preview={false}
                        onError={() => setBroken(true)}
                    />
                ) : (
                    <Typography.Text type="secondary">Unable to load</Typography.Text>
                )}
            </div>
            <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ fontSize: 12, marginBottom: 8 }}>
                {post.caption || '—'}
            </Typography.Paragraph>
            {draft && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <Button type="primary" size="small" loading={busy} onClick={() => onInclude?.(post)} style={{ flex: 1 }}>
                        Add to published
                    </Button>
                    <Button danger size="small" loading={busy} onClick={() => onDelete?.(post)} style={{ flex: 1 }}>
                        Remove
                    </Button>
                </div>
            )}
            {showRemove && (
                <Button danger size="small" block loading={busy} onClick={() => onExclude?.(post)} style={{ marginBottom: 8 }}>
                    Remove from campaign
                </Button>
            )}
            {isStory ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', textAlign: 'center', fontSize: 11, color: 'var(--text-muted)' }}>
                    <div>
                        <div style={metricNumber}>{savedViews ? formatVIT(post.vit) : '—'}</div>
                        VIT
                    </div>
                    <div>
                        <InputNumber
                            size="small"
                            min={0}
                            precision={0}
                            controls={false}
                            placeholder="—"
                            value={views}
                            onChange={(v) => setViews(v == null ? null : Number(v))}
                            onBlur={saveViews}
                            onPressEnter={saveViews}
                            style={{ width: 90, textAlign: 'center' }}
                        />
                        <div>VIEWS</div>
                    </div>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', textAlign: 'center', fontSize: 11, color: 'var(--text-muted)' }}>
                    <div><div style={metricNumber}>{formatVIT(post.vit)}</div>VIT</div>
                    <div><div style={metricNumber}>{formatCompact(post.likes)}</div>LIKES</div>
                    <div><div style={metricNumber}>{formatCompact(post.comments)}</div>COMMENTS</div>
                </div>
            )}
        </Card>
    );
};

export default PostCard;